import { useClassPerfBySubject } from '../../../api/hooks';
import type { ClassPerfBySubjectRow } from '../../../api/types';
import { Button, PanelCard, SortableTh, Table, Td } from '../../../components/ui';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import EmptyState from '../../../components/ui/EmptyState';
import { FaFileAlt } from '../../../components/ui/icons';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { Key, Legend, SubRow, dayMonth } from '../lib/cpBits';
import { TH_RULE } from '../../../lib/tableRules';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  /** Currently filtered subject, so its Filter button shows as pressed. */
  subjectId?: number | 'all';
  onPickSubject: (id: number | 'all') => void;
}

const WEAK_BELOW = 55;

const BY_SUBJECT_COLUMNS: SortColumn<ClassPerfBySubjectRow>[] = [
  { key: 'subject', accessor: (s) => s.name },
  { key: 'avg', accessor: (s) => s.avg_score, type: 'number' },
  { key: 'attempts', accessor: (s) => s.attempts_count, type: 'number' },
  { key: 'students', accessor: (s) => s.students_with_data, type: 'number' },
];

export default function BySubjectTab({ gradeId, sectionId, subjectId, onPickSubject }: Props) {
  const q = useClassPerfBySubject({ grade: gradeId, section: sectionId });
  // The API applies no explicit subject-level ordering (only `-week_start` to pick
  // the latest snapshot week), so the API's row order is arbitrary. Ruling: default
  // to subject name A→Z rather than mirroring that arbitrary order.
  const { rows, sort, toggle } = useSortableTable(q.data?.subjects, BY_SUBJECT_COLUMNS, { key: 'subject', dir: 'asc' });
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const d = q.data;
  if (d.subjects.length === 0) {
    return (
      <PanelCard>
        <EmptyState
          icon={<FaFileAlt />}
          title="No per-subject data yet"
          body="No subject data yet."
        />
      </PanelCard>
    );
  }
  return (
    <PanelCard
      title="Average by subject"
      help="tab_by_subject"
      align="end"
      subtitle={`${d.week_start ? `Week of ${dayMonth(d.week_start)} · ` : ''}pick a subject to filter the whole page`}
      actions={<Legend><Key tone="accent">{`Under ${WEAK_BELOW}%`}</Key><Key tone="slate">{`${WEAK_BELOW}% and up`}</Key></Legend>}
    >
      <Table minWidth={640}>
        <thead>
          <tr>
            <SortableTh columnKey="subject" sort={sort} onToggle={toggle} className={TH_RULE}>Subject</SortableTh>
            <SortableTh columnKey="avg" sort={sort} onToggle={toggle} help="avg_score" className={`${TH_RULE} w-[40%]`}>Average</SortableTh>
            <SortableTh columnKey="attempts" sort={sort} onToggle={toggle} align="right" help="attempts" className={TH_RULE}>Attempts</SortableTh>
            <SortableTh columnKey="students" sort={sort} onToggle={toggle} align="right" help="students_with_data" className={TH_RULE}>Students with data</SortableTh>
            <th className={`px-2.5 pb-2.5 ${TH_RULE}`}><span className="sr-only">Filter</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const pressed = subjectId === s.id;
            return (
              <tr key={s.id} className={pressed ? '[&>td]:bg-accent-bg/50' : ''}>
                <Td className="font-semibold">{s.name}</Td>
                <Td><SubRow value={s.avg_score} lo={s.avg_score != null && s.avg_score < WEAK_BELOW} label={`${s.name} average`} /></Td>
                <Td align="right" className="font-mono tabular-nums">{s.attempts_count}</Td>
                <Td align="right" className="font-mono tabular-nums">{s.students_with_data}</Td>
                <Td align="right">
                  <Button variant="ghost" size="sm" aria-pressed={pressed} aria-label={`Filter to ${s.name}`}
                    onClick={() => onPickSubject(pressed ? 'all' : s.id)}>
                    {pressed ? 'Filtered' : 'Filter'}
                  </Button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </PanelCard>
  );
}
